import React, { useState } from 'react';
import styled from 'styled-components';
import { Routes, Route } from 'react-router-dom';
import AccountList from './components/AccountList';
import AccountDetail from './components/AccountDetail';
import Stats from './components/Stats';
import Allowance from './components/Allowance';
import BucketList from './components/BucketList';
import AddAccountModal from './components/AddAccountModal';
import AddSalaryModal from './components/AddSalaryModal';
import MemoModal from './components/MemoModal';

const AppContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  /*
   * 여기서 font-family 를 다시 잡지 않는다.
   * 'NanumSquare' 는 어디서도 불러오지 않는 이름이라 통째로 sans-serif 로
   * 떨어졌고, 그러면 body 에 걸어 둔 'Noto Sans KR' 까지 같이 버려진다.
   * 안드로이드에서는 sans-serif 가 로보토라 숫자만 로보토, 한글은 시스템
   * 한국어 글꼴로 갈라져 "15분"이 "1 5분"처럼 어긋나 보였다.
   */
  position: relative;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 14px 0;
  
  h1 {
    color: #333;
    font-size: 20px;
    font-weight: bold;
    margin: 0;
  }
  
  .header-buttons {
    display: flex;
    gap: 10px;
  }
  
  .header-button {
    padding: 8px 16px;
    font-weight: bold;
    border-radius: 4px;
    cursor: pointer;
    border: none;
    background-color: #007bff;
    color: white;

    &:hover {
      background-color: #0056b3;
    }
  }

  .header-button-outline {
    padding: 7px 15px;
    font-weight: bold;
    font-size: 18px;
    line-height: 1;
    border-radius: 4px;
    cursor: pointer;
    border: 1px solid #007bff;
    background-color: transparent;
    color: #007bff;

    &:hover {
      background-color: rgba(0, 123, 255, 0.1);
    }
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 18px;
    }

    .header-button {
      padding: 6px 12px;
      font-size: 14px;
    }

    .header-button-outline {
      padding: 6px 12px;
    }
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
  
  a {
    text-decoration: none;
    color: #007bff;
    font-weight: bold;
    padding: 10px 0;
    
    &.active {
      color: #000;
      border-bottom: 3px solid #007bff;
    }
  }
  
  @media (max-width: 768px) {
    gap: 15px;
    overflow-x: auto;
    
    a {
      white-space: nowrap;
    }
  }
`;

function App() {
  const [isAddAccountModalOpen, setIsAddAccountModalOpen] = useState(false);
  const [isAddSalaryModalOpen, setIsAddSalaryModalOpen] = useState(false);
  const [isMemoModalOpen, setIsMemoModalOpen] = useState(false);

  // Listen for custom event to open add account modal
  React.useEffect(() => {
    const handleOpenModal = () => setIsAddAccountModalOpen(true);
    window.addEventListener('openAddAccountModal', handleOpenModal);
    return () => window.removeEventListener('openAddAccountModal', handleOpenModal);
  }, []);

  const handleAddAccount = () => {
    setIsAddAccountModalOpen(true);
  };

  const handleAddSalary = () => {
    setIsAddSalaryModalOpen(true);
  };

  const handleOpenMemo = () => {
    setIsMemoModalOpen(true);
  };

  const handleAccountAdded = () => {
    // Refresh the account list
    window.location.reload();
  };

  const handleSalaryAdded = () => {
    // Refresh the stats page if it's currently open
    window.location.reload();
  };

  return (
    <AppContainer>
      <Header>
        <h1>가계부</h1>
        <div className="header-buttons">
          <button className="header-button-outline" onClick={handleOpenMemo} title="메모">📝</button>
          <button className="header-button" onClick={handleAddSalary}>$월</button>
          <button className="header-button" onClick={handleAddAccount}>계좌 추가</button>
        </div>
      </Header>

      <Navigation>
        <a href="/">계좌 목록</a>
        <a href="/stats">통계</a>
        <a href="/allowance">용돈 모으기</a>
        <a href="/bucket">버킷리스트</a>
      </Navigation>

      <Routes>
        <Route path="/" element={<AccountList />} />
        <Route path="/account/:id" element={<AccountDetail />} />
        <Route path="/stats" element={<Stats />} />
        <Route path="/allowance" element={<Allowance />} />
        <Route path="/bucket" element={<BucketList />} />
      </Routes>

      <AddAccountModal
        isOpen={isAddAccountModalOpen}
        onClose={() => setIsAddAccountModalOpen(false)}
        onAccountAdded={handleAccountAdded}
      />

      <AddSalaryModal
        isOpen={isAddSalaryModalOpen}
        onClose={() => setIsAddSalaryModalOpen(false)}
        onSalaryAdded={handleSalaryAdded}
      />

      <MemoModal
        isOpen={isMemoModalOpen}
        onClose={() => setIsMemoModalOpen(false)}
      />
    </AppContainer>
  );
}

export default App;
